<template>
  <div>
    <AdminSubKreweSummary
      :subkrewe="currentSubkrewe"
      :member-count="memberCount + captainCount + redshirtCount + totalChildren"
      class="mb-5"
    />

    <!-- Same rule as the member browser (isSubKreweActive) -->
    <div
      v-if="hiddenForCaptainDues"
      class="kb-callout-warn mb-5"
      role="status"
    >
      <p class="m-0 font-semibold">
        <i class="pi pi-eye-slash !text-xs mr-1.5" />Closed to new members
      </p>
      <p class="m-0 mt-0.5 text-kb-fg">
        No captain has paid {{ paradeYear }} dues, so members can't find this SubKrewe in the browser to join it. It shows again once a captain pays.
      </p>
    </div>

    <Accordion
      multiple
      :value="activePanels"
      @update:value="onPanelsUpdate"
    >
      <AccordionPanel
        v-if="!isRedshirtAdmin"
        value="0"
      >
        <AccordionHeader>Admin Settings</AccordionHeader>
        <AccordionContent>
          <!-- Abandonment status indicator for administrators -->
          <div
            v-if="currentSubkrewe.abandoned"
            class="kb-callout-warn mt-3"
            role="status"
          >
            <p class="m-0 font-semibold">
              <i class="pi pi-flag !text-xs mr-1.5" />Abandoned<template v-if="abandonedAt">
                on {{ formatDate(abandonedAt) }}
              </template> by {{ abandonedByName }}
            </p>
            <p
              v-if="currentSubkrewe.abandonment_reason"
              class="m-0 mt-0.5 text-kb-fg"
            >
              {{ currentSubkrewe.abandonment_reason }}
            </p>
          </div>

          <div class="kb-rows">
            <AdminSubKreweAdminSection
              :key="`admin-${currentSubkrewe.id}`"
              :subkrewe="currentSubkrewe"
              @update="handleSubkreweUpdate"
              @delete="handleSubkreweDelete"
            />
            <AdminSubKreweExpectedCaptainsSection
              :key="`expected-captains-${currentSubkrewe.id}`"
              :subkrewe="currentSubkrewe"
            />
          </div>
        </AccordionContent>
      </AccordionPanel>

      <AccordionPanel value="1">
        <AccordionHeader>Captains</AccordionHeader>
        <AccordionContent>
          <AdminSubKreweCaptainsSection
            :key="`captains-${currentSubkrewe.id}`"
            :subkrewe="currentSubkrewe"
            :read-only="isRedshirtAdmin"
            @update="handleSubkreweUpdate"
          />
        </AccordionContent>
      </AccordionPanel>

      <AccordionPanel value="2">
        <AccordionHeader>
          <span class="flex items-center gap-3 w-full min-w-0 mr-3">
            <span>Members</span>
            <span class="kb-section-meta ml-auto">
              <span v-tooltip.top="{ value: 'Members', showDelay: 300 }"><i class="pi pi-users" />{{ memberCount }}</span>
              <span v-tooltip.top="{ value: 'Captains', showDelay: 300 }"><i class="pi pi-star" />{{ captainCount }}</span>
              <span v-tooltip.top="{ value: 'Children', showDelay: 300 }"><Icon
                name="lucide:baby"
                class="!leading-none"
              />{{ totalChildren }}</span>
              <span v-tooltip.top="{ value: 'Red shirts', showDelay: 300 }"><Icon
                name="simple-icons:startrek"
                class="!leading-none"
              />{{ redshirtCount }}</span>
              <span
                v-if="contraptionCount > 0"
                v-tooltip.top="{ value: 'Contraptions', showDelay: 300 }"
              ><i class="pi pi-box" />{{ contraptionCount }}</span>
              <span v-tooltip.top="{ value: 'Total (members + captains + red shirts + children)', showDelay: 300 }">Σ {{ memberCount + captainCount + redshirtCount + totalChildren }}</span>
              <span
                v-tooltip.top="{ value: 'Red shirt ratio', showDelay: 300 }"
                class="kb-tag"
                :class="ratioTag"
              >{{ formattedRatio }}</span>
            </span>
          </span>
        </AccordionHeader>
        <AccordionContent>
          <!--
            Emailing from here (rather than a mailto:) is the only path that
            sends from a domain registered with Apple, so Hide My Email members
            actually receive it. Red Shirt Admins get the same button but the
            composer and the endpoint both narrow them to Red Shirts.
          -->
          <SubkreweCaptainEmailComposer
            v-if="canEmailSubkrewe"
            v-model:visible="emailComposerVisible"
            :subkrewe="currentSubkrewe"
            :captain-email="loggedInMember?.email"
            :grant="emailGrant"
          />

          <!-- pb-1: the paginator's own padding makes up the rest of the
               spacing every drawer section leaves below its content -->
          <AdminSubKreweMembersSection
            :key="`members-${currentSubkrewe.id}`"
            class="pb-1"
            :subkrewe="currentSubkrewe"
            :is-redshirt-admin="isRedshirtAdmin"
            :is-admin="isAdmin"
            @update="handleSubkreweUpdate"
          >
            <template
              v-if="canEmailSubkrewe"
              #actions
            >
              <Button
                icon="pi pi-envelope"
                label="Email"
                size="small"
                text
                severity="secondary"
                @click="emailComposerVisible = true"
              />
            </template>
          </AdminSubKreweMembersSection>
        </AccordionContent>
      </AccordionPanel>

      <AccordionPanel value="3">
        <AccordionHeader>Subkrewe Details</AccordionHeader>
        <AccordionContent>
          <div :class="isRedshirtAdmin ? 'pointer-events-none opacity-60' : ''">
            <SharedSubKreweFormFields
              :key="`subkrewe-form-${currentSubkrewe.id}`"
              :data="currentSubkrewe"
              compact
              :show-terms-help="false"
              class="pt-3 pb-3"
              @update:title="$emit('update:title', $event)"
              @saved="onFormSaved"
            />
          </div>
          <p
            v-if="isRedshirtAdmin"
            class="kb-row-meta mt-2 mb-0"
          >
            Read-only for red shirt admins
          </p>
        </AccordionContent>
      </AccordionPanel>

      <AccordionPanel
        v-if="!isRedshirtAdmin"
        value="4"
      >
        <AccordionHeader>Pre-paid Memberships</AccordionHeader>
        <AccordionContent>
          <AdminSubKreweBulkPurchasesSection
            :key="`bulk-${currentSubkrewe.id}`"
            :subkrewe="currentSubkrewe"
            @update="handleSubkreweUpdate"
          />
        </AccordionContent>
      </AccordionPanel>

      <AccordionPanel
        v-if="canViewHistory"
        value="5"
      >
        <AccordionHeader>History</AccordionHeader>
        <AccordionContent>
          <AdminAuditHistory
            v-if="historyOpen"
            :key="`history-${currentSubkrewe.id}`"
            :ref-path="`subkrewes/${currentSubkrewe.id}`"
          />
        </AccordionContent>
      </AccordionPanel>
    </Accordion>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useRedshirtRatio } from '~/composables/useRedshirtRatio'
import type { SubKrewe } from '~/shared/types/subkrewe'
import type { SubkreweEmailGrant } from '~/shared/utils/subkreweEmail.js'
import { ratioTagClass } from '~/utils/subkreweDisplay'
import { canViewAuditHistory } from '~/shared/utils/grantPrivileges.js'
import { memberIsRedshirtAdmin, memberIsAdmin } from '~/shared/utils/memberUtils'
import { formatDate } from '~/utils/dateUtils'
import { isHiddenForCaptainDues } from '~/utils/subkreweUtils'

// The store makes `abandoned_at` a Date, or leaves it out while the server
// timestamp is pending.
const abandonedAt = computed(() =>
  currentSubkrewe.value?.abandoned_at instanceof Date ? currentSubkrewe.value.abandoned_at : null,
)

defineEmits(['update:title', 'close'])

interface Props {
  data: SubKrewe
}

const props = defineProps<Props>()
const subkrewesStore = useSubKrewesStore()
const { closeDrawer } = useDrawer()
const loggedInMember = await useCurrentMember()
const isRedshirtAdmin = computed(() => memberIsRedshirtAdmin(loggedInMember))
const isAdmin = computed(() => memberIsAdmin(loggedInMember))

// Captains reach their SubKrewe from /subkrewe; this is the surface admins and
// Red Shirt Admins use, so the composer needs an entry point here too.
const canEmailSubkrewe = computed(() => isAdmin.value || isRedshirtAdmin.value)
const emailGrant = computed<SubkreweEmailGrant>(() => (isAdmin.value ? 'admin' : 'redshirt_admin'))
const emailComposerVisible = ref(false)

// Get saved panel state from localStorage or use default [0,1]
const getStoredPanelState = (): string[] => {
  const stored = localStorage.getItem('subkrewe-drawer-panels')
  if (stored) {
    try {
      return JSON.parse(stored)
    }
    catch (e) {
      console.error('Error parsing stored panel state:', e)
      return ['0', '1']
    }
  }
  return ['0', '1'] // Default to first two panels open
}

// Active panels state with persistence
const activePanels = ref<string | string[] | null | undefined>(getStoredPanelState())

// Handle panel updates and save to localStorage
const onPanelsUpdate = (newValue: string | string[] | null | undefined) => {
  activePanels.value = newValue
  localStorage.setItem('subkrewe-drawer-panels', JSON.stringify(newValue))
}

const currentUser = useCurrentUser()
const canViewHistory = computed(() => canViewAuditHistory(loggedInMember, currentUser.value))
// Only query the log once the panel is opened.
const historyOpen = computed(() => [activePanels.value].flat().includes('5'))

// Current subkrewe reactive reference
const currentSubkrewe = computed(() => {
  if (props.data.id) {
    const path = `subkrewes/${props.data.id}`
    const storeSubkrewe = subkrewesStore.subkrewes[path]
    return storeSubkrewe || props.data
  }
  return props.data
})

const { paradeYear } = useParadeYear()
const hiddenForCaptainDues = computed(() => isHiddenForCaptainDues(currentSubkrewe.value))

// Add computed property for displaying the abandoned by user name
const abandonedByName = computed(() => {
  if (!currentSubkrewe.value?.abandoned_by) return 'Unknown'

  // Try to find the member who abandoned the SubKrewe
  const membersStore = useMembersStore()
  const memberPath = `members/${currentSubkrewe.value.abandoned_by}`
  const member = membersStore.members[memberPath]

  return member?.name || 'Unknown user'
})

// Calculate total children count across all members
const totalChildren = computed(() => {
  if (!currentSubkrewe.value?.members) return 0

  return currentSubkrewe.value.members.reduce((total, member) => {
    if (!member.children) return total
    return total + Object.keys(member.children).length
  }, 0)
})

// Redshirt counts and ratio for display
const redshirtCount = computed(() => {
  const members = currentSubkrewe.value?.members || []
  return members.filter(m => m.member_type === 'redshirt').length
})
const captainCount = computed(() => currentSubkrewe.value?.captains?.length || 0)
const memberCount = computed(() => {
  const list = currentSubkrewe.value?.members || []
  return list.filter(m => m.member_type === 'member').length
})
const contraptionCount = computed(() => {
  return currentSubkrewe.value?.contraption_count ?? (currentSubkrewe.value?.contraption ? 1 : 0)
})
const { computeRedshirtRatio, getRedshirtRatioSeverity, formatRedshirtRatio } = useRedshirtRatio()
// Ratio denominator includes members + captains + children
const redshirtRatio = computed(() =>
  computeRedshirtRatio(
    redshirtCount.value,
    memberCount.value + captainCount.value + totalChildren.value,
  ),
)
const ratioSeverity = computed(() => getRedshirtRatioSeverity(redshirtRatio.value, redshirtCount.value))
const formattedRatio = computed(() => formatRedshirtRatio(redshirtRatio.value))
const ratioTag = computed(() => ratioTagClass(ratioSeverity.value))

// Form saved handler
const onFormSaved = (_updatedSubkrewe: SubKrewe) => {
  // NOOP for now - already handling updates via reactivity
}

// Handle update events from child components
const handleSubkreweUpdate = (_update: Partial<SubKrewe>) => {
  // We don't need to do anything here as reactive properties
  // will automatically update and propagate changes
}

// Handle SubKrewe deletion event
const handleSubkreweDelete = () => {
  // Close the drawer when SubKrewe is deleted
  closeDrawer()
}

// Initialize and set up subscriptions
onMounted(() => {
  // Subscribe to subkrewe changes
  const unsubscribe = subkrewesStore.$subscribe((mutation, state) => {
    if (props.data.id) {
      const path = `subkrewes/${props.data.id}`
      if (state.subkrewes[path]) {
        // Force a component refresh
        nextTick(() => {
          // This access forces the computed property to re-evaluate
          const _ = currentSubkrewe.value
        })
      }
    }
  })

  // Clean up subscription
  onUnmounted(() => {
    unsubscribe()
  })
})
</script>
